/**
 * Resolve an order for A6 gift inscriptions.
 * Prefer an in-memory / Firestore-loaded order, then OrderDesk (source ID, then order number).
 * Preserves checkout_data and order_notes so giftCardCandidates can score real OD messages.
 */

import { buildUrl, fetchJson, stripODIN } from '../services/orderManagementService';

const ORDERDESKER_BASE =
  process.env.REACT_APP_ORDERDESKER_URL ||
  'https://orderdesker-844411985132.europe-west2.run.app';

export function normalizeInscriptionIdentifier(identifier) {
  return String(identifier || '')
    .trim()
    .replace(/^#/, '')
    .replace(/^ODIN-?/i, '')
    .trim();
}

export function unwrapInscriptionLookupOrder(response) {
  if (!response || typeof response !== 'object') return null;
  if (response.order && typeof response.order === 'object') return response.order;
  if (response.data?.order && typeof response.data.order === 'object') {
    return response.data.order;
  }
  if (
    response.source_id ||
    response.sourceId ||
    response.order_id ||
    response.order_number ||
    response.id
  ) {
    return response;
  }
  return null;
}

/**
 * Keep every note field OrderDesk may return so inscription scoring sees gift text.
 * Does not invent messages — only normalises shapes.
 */
export function normalizeOrderForInscriptions(raw, fallbackIdentifier = '') {
  if (!raw || typeof raw !== 'object') return null;

  const checkout = {
    ...(raw.checkout_data && typeof raw.checkout_data === 'object'
      ? raw.checkout_data
      : {}),
    ...(raw.checkoutData && typeof raw.checkoutData === 'object'
      ? raw.checkoutData
      : {}),
  };

  const orderNotes = Array.isArray(raw.order_notes)
    ? raw.order_notes
    : Array.isArray(raw.orderNotes)
      ? raw.orderNotes
      : [];

  const notesFromArray = orderNotes
    .map((note) => {
      if (typeof note === 'string') return note.trim();
      return String(
        note?.content || note?.note || note?.message || note?.text || '',
      ).trim();
    })
    .filter(Boolean);

  const notes =
    String(raw.notes || raw.note || raw.gift_message || raw.giftMessage || '')
      .trim() || notesFromArray.join('\n\n');

  const sourceId = String(
    raw.source_id ||
      raw.sourceId ||
      checkout['Source ID#'] ||
      checkout['Order Number'] ||
      raw.order_number ||
      raw.orderNumber ||
      fallbackIdentifier ||
      '',
  )
    .trim()
    .replace(/^#/, '');

  return {
    ...raw,
    source_id: sourceId || raw.source_id || raw.sourceId || '',
    sourceId: sourceId || raw.sourceId || raw.source_id || '',
    order_id: raw.order_id || raw.id || '',
    checkout_data: checkout,
    checkoutData: checkout,
    order_notes: orderNotes,
    notes,
    order_items: Array.isArray(raw.order_items)
      ? raw.order_items
      : Array.isArray(raw.items)
        ? raw.items
        : [],
    _inscriptionSource: raw._inscriptionSource || undefined,
  };
}

function orderMatchesIdentifier(order, wantedLower) {
  if (!order || !wantedLower) return false;
  return [
    order.id,
    order.orderId,
    order.order_id,
    order.order_number,
    order.orderNumber,
    order.source_id,
    order.sourceId,
  ].some(
    (value) =>
      normalizeInscriptionIdentifier(value).toLowerCase() === wantedLower,
  );
}

export function findLocalInscriptionOrder(localOrders, identifier) {
  const wanted = normalizeInscriptionIdentifier(identifier).toLowerCase();
  if (!wanted || !Array.isArray(localOrders)) return null;
  const match = localOrders.find((order) => orderMatchesIdentifier(order, wanted));
  return match ? normalizeOrderForInscriptions(match, wanted) : null;
}

async function fetchOrderDeskJson(url) {
  try {
    return await fetchJson(url);
  } catch (error) {
    if (error?.status === 404) return null;
    throw error;
  }
}

/**
 * Fetch a live OrderDesk order by source ID or OrderDesk order id / number.
 * @returns {Promise<object|null>}
 */
export async function fetchInscriptionOrderFromOrderDesk(identifier) {
  const value = normalizeInscriptionIdentifier(identifier);
  if (!value) return null;

  const attempts = [
    `${ORDERDESKER_BASE.replace(/\/$/, '')}/orders/find/by-source/${encodeURIComponent(value)}`,
    buildUrl('/', { source_id: stripODIN(value) }),
    buildUrl('/', { order_id: value }),
  ];

  let lastError = null;
  for (const url of attempts) {
    try {
      const response = await fetchOrderDeskJson(url);
      const raw = unwrapInscriptionLookupOrder(response);
      if (!raw) continue;
      const normalized = normalizeOrderForInscriptions(raw, value);
      if (normalized) {
        return { ...normalized, _inscriptionSource: 'orderDesk' };
      }
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) throw lastError;
  return null;
}

/**
 * Local list first (Firestore / already loaded folder), then OrderDesk.
 */
export async function lookupInscriptionOrder(identifier, { localOrders = [] } = {}) {
  const value = normalizeInscriptionIdentifier(identifier);
  if (!value) {
    throw new Error('Enter an order number or source ID.');
  }

  const local = findLocalInscriptionOrder(localOrders, value);
  if (local) {
    return { ...local, _inscriptionSource: local._inscriptionSource || 'local' };
  }

  const fromOrderDesk = await fetchInscriptionOrderFromOrderDesk(value);
  if (fromOrderDesk) return fromOrderDesk;

  throw new Error(
    `No order found for "${value}" in the loaded list or OrderDesk. Check the source ID / order number.`,
  );
}

export default lookupInscriptionOrder;
